import { t } from "@lingui/macro";
import { uploadFiles } from "@winamp/media-upload/upload-files.js";
import { useToastContext } from "@winamp/ui/components/Toast/use-toast-context.js";
import { useState } from "react";
import type { Accept, DropzoneOptions } from "react-dropzone-esm";
import { useOptionalAppContext } from "~/modules/auth/hooks";
import { useBytescaleConfig } from "~/modules/bytescale/bytescale";
import { getDropzoneErrorMessages } from "~/modules/media/get-dropzone-error-messages";
import { useHasPermissions } from "~/modules/permissions/helpers";

type UseMediaDropzoneOptionsArgs = {
  onDropSuccess?: () => void;
  key?: string;
  showSuccessToast?: boolean;
};

export function useMediaDropzoneOptions({
  onDropSuccess,
  key = "media-uploads",
  showSuccessToast = false,
}: UseMediaDropzoneOptionsArgs = {}) {
  const bytescaleConfig = useBytescaleConfig();
  const [dropErrors, setDropErrors] = useState<Array<string>>([]);
  const auth = useOptionalAppContext();
  const canUpload = useHasPermissions(["Media.Upload"]);
  const { addToast } = useToastContext();

  async function uploadAcceptedFiles(acceptedFiles: Array<File>) {
    onDropSuccess?.();
    await uploadFiles({
      key,
      files: acceptedFiles,
      parallelUploads: 3,
      publicKey: bytescaleConfig.publicKey,
      folderPath: bytescaleConfig.folderPath.media,
      accountId: bytescaleConfig.accountId,
      canUpload: bytescaleConfig.canUpload,
    });

    if (showSuccessToast) {
      addToast({
        type: "success",
        title: t`Success`,
        message:
          acceptedFiles.length > 1
            ? t`${acceptedFiles.length} files have been uploaded successfully.`
            : t`${acceptedFiles[0].name} has been uploaded successfully.`,
      });
    }
  }

  const accept: Accept = {
    "audio/wav": [".wav"],
    "audio/x-wav": [".wav"],
    "application/x-wav": [".wav"],
    "audio/x-flac": [".flac"],
    "audio/flac": [".flac"],
    "application/x-flac": [".flac"],
    "audio/mpeg": [".mp3"],
    "audio/aac": [".aac"],
    "audio/pcm": [".pcm"],
  };

  const dropzoneOptions: DropzoneOptions = {
    disabled: !auth?.artist || !canUpload,
    accept,
    onDrop(acceptedFiles, rejectedFiles) {
      setDropErrors(getDropzoneErrorMessages(rejectedFiles, { accept }));
      if (acceptedFiles.length > 0) {
        uploadAcceptedFiles(acceptedFiles);
      }
    },
  };

  return { dropzoneOptions, dropErrors };
}
